import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle } from "vue";
const _hoisted_1 = ["disabled", "aria-pressed", "aria-expanded"];
const _hoisted_2 = ["aria-label"];
const _hoisted_3 = { class: "text-lg font-semibold text-eblack-900" };
const _hoisted_4 = { class: "mt-1 text-sm text-eblack-500" };
const _hoisted_5 = {
    class: "mt-6 flex h-16 items-end gap-[2px] px-3",
    "aria-hidden": "true"
};
const _hoisted_6 = ["data-in-range"];
const _hoisted_7 = { class: "price-range-slider relative h-6" };
const _hoisted_8 = ["min", "max", "value", "aria-label"];
const _hoisted_9 = ["min", "max", "value", "aria-label"];
const _hoisted_10 = { class: "mt-5 flex items-center gap-4" };
const _hoisted_11 = { class: "flex flex-1 flex-col rounded-xl border border-ewhite-300 px-3 py-2 focus-within:border-eblack-900" };
const _hoisted_12 = { class: "text-xs text-eblack-500" };
const _hoisted_13 = { class: "flex items-center gap-0.5 text-sm text-eblack-900" };
const _hoisted_14 = ["min", "max", "value"];
const _hoisted_15 = { class: "flex flex-1 flex-col rounded-xl border border-ewhite-300 px-3 py-2 focus-within:border-eblack-900" };
const _hoisted_16 = { class: "text-xs text-eblack-500" };
const _hoisted_17 = { class: "flex items-center gap-0.5 text-sm text-eblack-900" };
const _hoisted_18 = ["min", "max", "value"];
const _hoisted_19 = { class: "mt-6 flex items-center justify-between border-t border-ewhite-200 pt-4" };
import { computed, onBeforeUnmount, reactive, ref, watch } from "vue";
const CURRENCY_SYMBOL = "$";
const BUCKET_COUNT = 24;
export default /*@__PURE__*/ _defineComponent({
    __name: 'PriceRangeFilter',
    props: {
        prices: {},
        modelValue: {},
        countFor: { type: Function }
    },
    emits: ["update:modelValue"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const rootRef = ref(null);
        const isOpen = ref(false);
        const hasPrices = computed(() => props.prices.length > 0);
        const bounds = computed(() => {
            if (!hasPrices.value)
                return { min: 0, max: 0 };
            return {
                min: Math.floor(Math.min(...props.prices)),
                max: Math.ceil(Math.max(...props.prices)),
            };
        });
        const draft = reactive({ min: 0, max: 0 });
        function clamp(value, low, high) {
            return Math.min(Math.max(value, low), high);
        }
        function resetDraft() {
            const { min, max } = bounds.value;
            draft.min = clamp(props.modelValue?.min ?? min, min, max);
            draft.max = clamp(props.modelValue?.max ?? max, draft.min, max);
        }
        /** Draft range, or null when it spans every price (i.e. no filtering). */
        const normalizedDraft = computed(() => draft.min <= bounds.value.min && draft.max >= bounds.value.max
            ? null
            : { min: draft.min, max: draft.max });
        const previewCount = computed(() => props.countFor(normalizedDraft.value));
        function formatUsd(value) {
            return `${CURRENCY_SYMBOL}${value}`;
        }
        const triggerLabel = computed(() => props.modelValue
            ? `${formatUsd(props.modelValue.min)} – ${formatUsd(props.modelValue.max)}`
            : "");
        const histogram = computed(() => {
            const { min, max } = bounds.value;
            const span = max - min;
            const counts = new Array(BUCKET_COUNT).fill(0);
            for (const price of props.prices) {
                const index = span === 0
                    ? 0
                    : Math.min(BUCKET_COUNT - 1, Math.floor(((price - min) / span) * BUCKET_COUNT));
                counts[index] += 1;
            }
            const peak = Math.max(...counts, 1);
            const width = span / BUCKET_COUNT;
            return counts.map((count, index) => {
                const start = min + index * width;
                const end = start + width;
                return {
                    height: count === 0 ? 0 : Math.max(8, (count / peak) * 100),
                    inRange: end >= draft.min && start <= draft.max,
                };
            });
        });
        const selectedTrackStyle = computed(() => {
            const { min, max } = bounds.value;
            const span = max - min || 1;
            const left = ((draft.min - min) / span) * 100;
            const right = ((max - draft.max) / span) * 100;
            return {
                left: `calc(0.75rem + (100% - 1.5rem) * ${left / 100})`,
                right: `calc(0.75rem + (100% - 1.5rem) * ${right / 100})`,
            };
        });
        function readNumber(event) {
            const target = event.target;
            return { target, value: Number(target.value) };
        }
        function setMin(value, target) {
            const fallback = Number.isFinite(value) ? value : bounds.value.min;
            draft.min = clamp(Math.round(fallback), bounds.value.min, draft.max);
            if (target)
                target.value = String(draft.min);
        }
        function setMax(value, target) {
            const fallback = Number.isFinite(value) ? value : bounds.value.max;
            draft.max = clamp(Math.round(fallback), draft.min, bounds.value.max);
            if (target)
                target.value = String(draft.max);
        }
        function handleMinSlider(event) {
            const { target, value } = readNumber(event);
            setMin(value, target);
        }
        function handleMaxSlider(event) {
            const { target, value } = readNumber(event);
            setMax(value, target);
        }
        function handleMinInput(event) {
            const { target, value } = readNumber(event);
            setMin(target.value === "" ? NaN : value, target);
        }
        function handleMaxInput(event) {
            const { target, value } = readNumber(event);
            setMax(target.value === "" ? NaN : value, target);
        }
        function handleDocumentPointer(event) {
            if (rootRef.value && !rootRef.value.contains(event.target))
                close();
        }
        function handleDocumentKeydown(event) {
            if (event.key === "Escape")
                close();
        }
        function open() {
            if (!hasPrices.value)
                return;
            resetDraft();
            isOpen.value = true;
        }
        function close() {
            isOpen.value = false;
        }
        function toggle() {
            if (isOpen.value)
                close();
            else
                open();
        }
        function handleApply() {
            emit("update:modelValue", normalizedDraft.value);
            close();
        }
        function handleClear() {
            emit("update:modelValue", null);
            close();
        }
        watch(isOpen, (value) => {
            if (value) {
                document.addEventListener("mousedown", handleDocumentPointer);
                document.addEventListener("keydown", handleDocumentKeydown);
            }
            else {
                document.removeEventListener("mousedown", handleDocumentPointer);
                document.removeEventListener("keydown", handleDocumentKeydown);
            }
        });
        onBeforeUnmount(() => {
            document.removeEventListener("mousedown", handleDocumentPointer);
            document.removeEventListener("keydown", handleDocumentKeydown);
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                ref_key: "rootRef",
                ref: rootRef,
                class: "relative"
            }, [
                _createElementVNode("button", {
                    type: "button",
                    "data-testid": "price-filter-trigger",
                    class: _normalizeClass(["rounded-full border px-4 py-1.5 text-[13px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-50",
                        __props.modelValue
                            ? 'border-eblack-900 bg-white text-eblack-900 shadow-[inset_0_0_0_1px_#333333]'
                            : 'border-ewhite-300 bg-white text-eblack-700 hover:border-eblack-300'
                    ]),
                    disabled: !hasPrices.value,
                    "aria-pressed": __props.modelValue ? 'true' : 'false',
                    "aria-expanded": isOpen.value ? 'true' : 'false',
                    "aria-haspopup": "dialog",
                    onClick: toggle
                }, _toDisplayString(triggerLabel.value || _ctx.$t("services.auto.precio")), 11, _hoisted_1),
                (isOpen.value)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        "data-testid": "price-filter-backdrop",
                        class: "fixed inset-0 z-20 bg-black/20",
                        "aria-hidden": "true",
                        onClick: close
                    }))
                    : _createCommentVNode("", true),
                (isOpen.value)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 1,
                        "data-testid": "price-filter-panel",
                        role: "dialog",
                        "aria-label": _ctx.$t('services.auto.rango_de_precios'),
                        class: "fixed inset-x-4 top-1/2 z-30 mx-auto max-h-[calc(100dvh-2rem)] max-w-md -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-5 shadow-[0_8px_28px_rgba(0,0,0,0.18)] sm:absolute sm:inset-x-auto sm:left-0 sm:top-full sm:mx-0 sm:mt-2 sm:max-h-none sm:w-[400px] sm:max-w-none sm:translate-y-0 sm:overflow-visible sm:border sm:border-ewhite-200 sm:p-6 sm:shadow-[0_8px_28px_rgba(0,0,0,0.14)]"
                    }, [
                        _createElementVNode("h3", _hoisted_3, _toDisplayString(_ctx.$t("services.auto.rango_de_precios")), 1),
                        _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.$t("services.auto.rango_de_precios_ayuda")), 1),
                        _createElementVNode("div", _hoisted_5, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(histogram.value, (bar, index) => {
                                return (_openBlock(), _createElementBlock("div", {
                                    key: index,
                                    "data-testid": "price-histogram-bar",
                                    "data-in-range": bar.inRange ? 'true' : 'false',
                                    class: _normalizeClass(["flex-1 rounded-t-sm transition-colors", bar.inRange ? 'bg-eblack-800' : 'bg-eblack-200']),
                                    style: _normalizeStyle({ height: `${bar.height}%` })
                                }, null, 14, _hoisted_6));
                            }), 128))
                        ]),
                        _createElementVNode("div", _hoisted_7, [
                            _cache[0] || (_cache[0] = _createElementVNode("div", { class: "absolute inset-x-3 top-1/2 h-0.5 -translate-y-1/2 rounded bg-eblack-200" }, null, -1)),
                            _createElementVNode("div", {
                                class: "absolute top-1/2 h-0.5 -translate-y-1/2 rounded bg-eblack-900",
                                style: _normalizeStyle(selectedTrackStyle.value)
                            }, null, 4),
                            _createElementVNode("input", {
                                type: "range",
                                min: bounds.value.min,
                                max: bounds.value.max,
                                step: "1",
                                value: draft.min,
                                "aria-label": _ctx.$t('services.auto.precio_minimo'),
                                class: "absolute inset-0 h-6 w-full",
                                onInput: handleMinSlider
                            }, null, 40, _hoisted_8),
                            _createElementVNode("input", {
                                type: "range",
                                min: bounds.value.min,
                                max: bounds.value.max,
                                step: "1",
                                value: draft.max,
                                "aria-label": _ctx.$t('services.auto.precio_maximo'),
                                class: "absolute inset-0 h-6 w-full",
                                onInput: handleMaxSlider
                            }, null, 40, _hoisted_9)
                        ]),
                        _createElementVNode("div", _hoisted_10, [
                            _createElementVNode("label", _hoisted_11, [
                                _createElementVNode("span", _hoisted_12, _toDisplayString(_ctx.$t("services.auto.minimo")), 1),
                                _createElementVNode("span", _hoisted_13, [
                                    _createElementVNode("span", null, _toDisplayString(CURRENCY_SYMBOL)),
                                    _createElementVNode("input", {
                                        "data-testid": "price-filter-min",
                                        type: "number",
                                        inputmode: "numeric",
                                        min: bounds.value.min,
                                        max: bounds.value.max,
                                        value: draft.min,
                                        class: "w-full border-0 p-0 text-base text-eblack-900 focus:ring-0 sm:text-sm",
                                        onChange: handleMinInput
                                    }, null, 40, _hoisted_14)
                                ])
                            ]),
                            _cache[1] || (_cache[1] = _createElementVNode("span", {
                                class: "h-px w-4 bg-eblack-300",
                                "aria-hidden": "true"
                            }, null, -1)),
                            _createElementVNode("label", _hoisted_15, [
                                _createElementVNode("span", _hoisted_16, _toDisplayString(_ctx.$t("services.auto.maximo")), 1),
                                _createElementVNode("span", _hoisted_17, [
                                    _createElementVNode("span", null, _toDisplayString(CURRENCY_SYMBOL)),
                                    _createElementVNode("input", {
                                        "data-testid": "price-filter-max",
                                        type: "number",
                                        inputmode: "numeric",
                                        min: bounds.value.min,
                                        max: bounds.value.max,
                                        value: draft.max,
                                        class: "w-full border-0 p-0 text-base text-eblack-900 focus:ring-0 sm:text-sm",
                                        onChange: handleMaxInput
                                    }, null, 40, _hoisted_18)
                                ])
                            ])
                        ]),
                        _createElementVNode("div", _hoisted_19, [
                            _createElementVNode("button", {
                                type: "button",
                                "data-testid": "price-filter-clear",
                                class: "rounded-lg px-2 py-1.5 text-sm font-semibold text-eblack-900 underline underline-offset-2 hover:bg-ewhite-100",
                                onClick: handleClear
                            }, _toDisplayString(_ctx.$t("services.auto.borrar")), 1),
                            _createElementVNode("button", {
                                type: "button",
                                "data-testid": "price-filter-apply",
                                class: "rounded-lg bg-eblack-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-black active:scale-95",
                                onClick: handleApply
                            }, _toDisplayString(_ctx.$t("services.auto.mostrar_n_servicios", { count: previewCount.value })), 1)
                        ])
                    ], 8, _hoisted_2))
                    : _createCommentVNode("", true)
            ], 512));
        };
    }
});
